import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { NAV_LINKS } from "@/data/site";
import { goToAnchor } from "@/lib/scroll";
import { EASE } from "@/components/Reveal";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    setOpen(false);
    goToAnchor(href);
  };

  return (
    <>
      <header
        data-testid="main-nav"
        className={`fixed top-0 inset-x-0 z-[70] transition-[background-color,padding,border-color,backdrop-filter] duration-500 ${
          scrolled
            ? "bg-ink/80 backdrop-blur-xl border-b hairline py-3"
            : "bg-transparent py-6"
        }`}
      >
        <div className="max-w-[1500px] mx-auto px-6 md:px-12 flex items-center justify-between">
          <button
            data-testid="nav-logo"
            onClick={() => go("#top")}
            className="flex items-center gap-3 text-sm md:text-base font-semibold tracking-[0.4em] text-ivory"
          >
            <img src="/assets/vsipl-logo.png" alt="VSIPL — Vcontractz Solutions India Pvt. Ltd. logo" className="h-9 w-auto" />
            VCONTRACTZ<span className="text-gold">.</span>
          </button>

          <nav className="hidden lg:flex items-center gap-8" aria-label="Primary">
            {NAV_LINKS.map((l) => (
              <button
                key={l.href}
                data-testid={`nav-link-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                onClick={() => go(l.href)}
                className="text-[11px] uppercase tracking-[0.2em] text-ivory/70 hover:text-gold transition-colors duration-300"
              >
                {l.label}
              </button>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            <button
              data-testid="nav-get-quote-btn"
              onClick={() => go("#pricing")}
              className="text-[11px] uppercase tracking-[0.2em] text-ivory/70 hover:text-gold transition-colors duration-300 px-4 py-2"
            >
              Get a Quote
            </button>
            <button
              data-testid="nav-start-project-btn"
              onClick={() => go("#contact")}
              className="btn-primary !px-6 !py-3"
            >
              Start a Project
              <ArrowUpRight size={14} />
            </button>
          </div>

          <button
            data-testid="nav-mobile-menu-btn"
            className="lg:hidden text-ivory p-2"
            onClick={() => setOpen(true)}
            aria-label="Open menu"
          >
            <Menu size={22} />
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            className="fixed inset-0 z-[80] bg-ink flex flex-col"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
          >
            <div className="flex items-center justify-between px-6 py-6">
              <span className="flex items-center gap-3 text-sm font-semibold tracking-[0.4em]">
                <img src="/assets/vsipl-logo.png" alt="VSIPL logo" className="h-8 w-auto" />
                VCONTRACTZ<span className="text-gold">.</span>
              </span>
              <button data-testid="mobile-menu-close-btn" onClick={() => setOpen(false)} aria-label="Close menu" className="p-2">
                <X size={24} />
              </button>
            </div>
            <nav className="flex-1 flex flex-col justify-center px-8 gap-2" aria-label="Mobile">
              {NAV_LINKS.map((l, i) => (
                <motion.button
                  key={l.href}
                  data-testid={`mobile-nav-link-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 + i * 0.06, duration: 0.6, ease: EASE }}
                  onClick={() => go(l.href)}
                  className="text-left font-display text-4xl font-light text-ivory/90 hover:text-gold transition-colors duration-300 py-2 border-b hairline"
                >
                  {l.label}
                </motion.button>
              ))}
              <motion.button
                data-testid="mobile-start-project-btn"
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6, duration: 0.6, ease: EASE }}
                onClick={() => go("#contact")}
                className="btn-primary mt-8 self-start"
              >
                Start a Project
                <ArrowUpRight size={14} />
              </motion.button>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
